// Comparing a product's selected sizes with the sizes its supplier offers.
//
// XXL and 2XL name the same size in different catalogs: SanMar's GetProduct
// reports XXL for some styles (L500, LK240, LPC61) while the EPDD and our saved
// selected_sizes say 2XL. Every comparison here goes through canonicalSize, a
// mirror of the backend's one alias map (api/src/pricing/price_for.py
// _SIZE_ALIASES, re-exported as canonical_size by
// api/src/services/manual_blank_cost.py). Labels are never rewritten -- only
// compared -- so stored data keeps whatever spelling it was saved with.

const SIZE_ALIASES = Object.freeze({ XXL: '2XL', XXXL: '3XL', XXXXL: '4XL' })

export function canonicalSize(size) {
    if (size == null) return null
    const text = String(size).trim().toUpperCase()
    if (!text) return null
    return SIZE_ALIASES[text] ?? text
}

/** The label in `list` naming the same size as `size`, or null. */
export function findSameSize(list, size) {
    const wanted = canonicalSize(size)
    if (wanted == null || !Array.isArray(list)) return null
    return list.find((s) => canonicalSize(s) === wanted) ?? null
}

/**
 * The supplier's size run from a product-meta payload, or null when it is
 * not known. An empty list counts as unknown: nothing can be judged against
 * it, and treating it as "offers nothing" would flag every size.
 */
export function supplierSizeRun(meta) {
    const sizes = meta?.sizes
    return Array.isArray(sizes) && sizes.length > 0 ? sizes : null
}

/** The selected sizes the supplier does not offer; [] when the run is unknown. */
export function sizesNotOffered(selected, run) {
    if (!Array.isArray(selected) || !Array.isArray(run) || run.length === 0) return []
    return selected.filter((s) => findSameSize(run, s) == null)
}

/** The candidate sizes the supplier offers, in candidate order and spelling. */
export function sizesOffered(candidates, run) {
    if (!Array.isArray(candidates) || !Array.isArray(run)) return []
    return candidates.filter((s) => findSameSize(run, s) != null)
}
